import { estClient } from './estClient';

export type DocumentTemplate = {
  id: string;
  name: string;
  object_id: string;
  entity_type_id: string;
  language_id: string;
  filename_original: string;
  file_url: string;
  created_at: string | null;
  created_by: string;
  updated_at: string | null;
  updated_by: string;
};

export type DocumentImageValue = {
  path: string | null;
  width?: string;
  height?: string;
  ratio?: boolean;
};

export type TemplateVariable = {
  fieldName: string;
  fieldDescription?: string;
  value: string | number | null | DocumentImageValue | object;
  /**
   * Custom field type from DB (see CF_TYPE). Null for regular variables
   */
  cfType: number | null;
  /**
   * Options for CF_TYPE.CHOICE fields. Null for other field types
   */
  cfOptions: string[] | null;
  isSupported: boolean;
  decorators: SupportedDecorator[];
};

/**
 * Values for document's own custom fields, keyed by variable name without the
 * `doc.` prefix (i.e. `cf_comment` for the `${doc.cf_comment}` variable)
 */
export type DocumentFieldValues = Record<string, string>;

export type PreviewResponse = {
  success: boolean;
  variables: TemplateVariable[];
};

type TemplatesResponse = {
  success: boolean;
  document_template: DocumentTemplate[];
};

type GenerateResponse = {
  success: boolean;
  file_url: string;
};

/**
 * Template with `object_id`, `language_id` and `entity_type_id` resolved to
 * their names, so it is displayed but not edited
 */
export type DocumentTemplateView = DocumentTemplate & {
  filepath: string;
};

export type FoundVariable = {
  fieldName: string;
  isSupported: boolean;
};

export type SupportedVariable = {
  name: string;
  entityName: string;
  description: string;
};

export type SupportedDecorator = {
  name: string;
  description: string;
};

export type TemplateOption = {
  id: number;
  name: string;
};

export type TemplatePayload = {
  name: string;
  object_id: number;
  entity_type_id: number;
  language_id: number;
  template?: File;
};

type AnnotationResponse = {
  success: boolean;
  template: DocumentTemplateView;
  variables: FoundVariable[];
};

type SupportedVariablesResponse = {
  success: boolean;
  variables: SupportedVariable[];
  decorators: SupportedDecorator[];
  currenciesByLanguage: Record<string, string[] | null>;
};

type TemplateOptionsResponse = {
  success: boolean;
  objects: TemplateOption[];
  entityTypes: TemplateOption[];
  languages: TemplateOption[];
};

type GenerateParams = { unit_id: string } | { deal_id: string };

class DocumentApi {
  getTemplates(): Promise<TemplatesResponse> {
    return estClient.get<TemplatesResponse>('/api/db/document/template/');
  }

  getTemplateAnnotation(templateId: string): Promise<AnnotationResponse> {
    return estClient.get<AnnotationResponse>(
      `/api/fe/document/template/annotation/${templateId}`,
    );
  }

  getSupportedVariables(): Promise<SupportedVariablesResponse> {
    return estClient.get<SupportedVariablesResponse>(
      '/api/fe/document/template/supported-variables',
    );
  }

  getTemplateOptions(): Promise<TemplateOptionsResponse> {
    return estClient.get<TemplateOptionsResponse>(
      '/api/fe/document/template/options',
    );
  }

  deleteTemplate(id: string): Promise<{ success: boolean }> {
    return estClient.delete(`/api/db/document/template/${id}`);
  }

  /**
   * Creates a template when `id` is omitted, updates it otherwise. Sent as
   * `multipart/form-data` since `PUT` in PHP does not populate `$_FILES`
   */
  saveTemplate({
    id,
    ...data
  }: TemplatePayload & { id?: string }): Promise<{ success: boolean }> {
    return estClient.post(`/api/db/document/template/${id ?? ''}`, {
      body: data,
      bodyFormat: 'form-data',
    });
  }

  getPreview(
    templateId: string,
    params: { unit_id: string } | { deal_id: string },
  ): Promise<PreviewResponse> {
    return estClient.get<PreviewResponse>(
      `/api/fe/document/template/preview/${templateId}`,
      { query: params },
    );
  }

  async generate(
    templateId: string,
    params: GenerateParams,
    docFields: DocumentFieldValues = {},
  ): Promise<GenerateResponse | Blob> {
    const body = new URLSearchParams(params);

    // PHP reads `doc[cf_xxx]` as $_POST['doc']['cf_xxx']
    Object.entries(docFields).forEach(([name, value]) => {
      body.append(`doc[${name}]`, value);
    });

    const csrfToken = estClient.getCsrfToken();

    if (csrfToken) {
      body.append('token', csrfToken);
    }

    const resp = await fetch(
      `/api/fe/document/template/generate/${templateId}`,
      {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      },
    );

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `HTTP ${resp.status}`);
    }

    const contentType = resp.headers.get('Content-Type') ?? '';

    if (
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/vnd.openxmlformats-officedocument')
    ) {
      return resp.blob();
    }

    return resp.json() as Promise<GenerateResponse>;
  }

  get ENTITY_TYPE() {
    return {
      LEAD: '1',
      UNIT: '2',
      DEAL: '3',
    } as const;
  }

  /**
   * Custom field types, mirrors `Custom_field::FIELD_TYPE_*`
   */
  get CF_TYPE() {
    return {
      TEXT: 1,
      NUMBER: 2,
      DATE: 3,
      CHOICE: 4,
    } as const;
  }

  /**
   * Prefix of the document's own entity
   */
  get DOC_ENTITY_PREFIX() {
    return 'doc.';
  }

  /**
   * Variables the user has to fill in before generation - document's own custom fields
   */
  isDocField(variable: TemplateVariable): boolean {
    return (
      variable.isSupported &&
      variable.cfType !== null &&
      variable.fieldName.startsWith(this.DOC_ENTITY_PREFIX)
    );
  }

  /**
   * `doc.cf_comment` -> `cf_comment`. Modifiers (`|string`) are stripped
   */
  docFieldName(fieldName: string): string {
    return fieldName.slice(this.DOC_ENTITY_PREFIX.length).split('|')[0].trim();
  }
}

export const documentApi = new DocumentApi();
